import { call } from '@advanza/api'
import { PROFILE_STATUS_ENABLED } from '@advanza/constants'
import { objectKeys } from '@advanza/func'
import { Col, Row } from '@advanza/grid'
import { changeEntity } from '@advanza/redux_entity'
import { Divider, Toggle } from '@advanza/ui'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import { capitalize } from '@trustoo/funcs/string'
import clsx from 'clsx'
import HappinessIcon, { happinessColorMap } from 'components/ui/HappinessIcon'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useProvider } from 'hooks/providerHooks'
import { SatisfactionScore } from 'interfaces/Providers'
import { Fragment, ReactNode } from 'react'
import {
    CompareValue,
    SatisfactionRuleCategories,
} from 'reducers/satisfactionRuleCategoriesReducer'
import style from './customerSatisfaction.module.css'

const categoryTitleMap = {
    profile: 'Profile',
    leads: 'Leads received',
    response: 'Response %',
    contact: 'Chosen & reclaims',
    invoices: 'Invoices',
} as const

// noinspection JSUnusedGlobalSymbols
const customFieldValueDisplay = {
    profile_total_score: (val: any) => val * 10,
    profile_status: (val: any) => (val === PROFILE_STATUS_ENABLED ? 'Enabled' : 'Other'),
}

export const useCustomerSatisfaction = (providerId: number) => {
    const dispatch = useAppDispatch()
    const { provider } = useProvider(providerId)
    const satisfactionRuleCategories = useAppSelector(
        (state) => state.satisfactionRuleCategories.satisfactionRuleCategories,
    )

    const setIsLoading = (isLoading: boolean) =>
        dispatch(
            changeEntity({
                store: 'providers',
                name: 'providers',
                key: providerId,
                diff: { _isLoadingSatisfaction: isLoading },
            }),
        )

    const setScore = (score: SatisfactionScore) =>
        dispatch(
            changeEntity({
                store: 'providers',
                name: 'providers',
                key: providerId,
                diff: { last_satisfaction_score: score },
            }),
        )

    const getSatisfactionRuleCategories = async () => {
        if (satisfactionRuleCategories) {
            return
        }

        const response: { satisfactionRuleCategories: SatisfactionRuleCategories } = await call(
            'office/providers/get-satisfaction-rule-categories',
        )
        dispatch({
            type: 'RECEIVE_SATISFACTION_RULE_CATEGORIES',
            satisfactionRuleCategories: response.satisfactionRuleCategories,
        })
    }

    const getHappinessCalculation = async () => {
        getSatisfactionRuleCategories().then(() => {})

        if (!provider?.last_satisfaction_score) {
            await recalculateHappiness()
        } else if (!provider.last_satisfaction_score.resultsObj) {
            setIsLoading(true)
            const { score }: { score: SatisfactionScore } = await call(
                'office/providers/get-happiness-calculation',
                {
                    json: { lastSatisfactionScore: provider.last_satisfaction_score },
                },
            )
            setScore(score)
            setIsLoading(false)
        }
    }

    const recalculateHappiness = async () => {
        setIsLoading(true)
        const { score }: { score: SatisfactionScore } = await call(
            'office/providers/recalculate-happiness',
            {
                json: { providerId },
            },
        )
        setScore(score)
        setIsLoading(false)
    }

    return {
        satisfactionRuleCategories: satisfactionRuleCategories || {},
        getHappinessCalculation,
        recalculateHappiness,
    }
}

interface Props {
    providerId: number
}

const CustomerSatisfaction = ({ providerId }: Props) => {
    const { provider } = useProvider(providerId)
    const { satisfactionRuleCategories, getHappinessCalculation } =
        useCustomerSatisfaction(providerId)

    return provider ? (
        objectKeys(categoryTitleMap).map((categoryName, i) => (
            <Fragment key={categoryName}>
                {0 < i && <Divider sm />}
                <Toggle
                    extraClasses={[style.toggle]}
                    extraTitleClasses={[style.toggleTitle]}
                    disabled={provider._isLoadingSatisfaction}
                    onBeforeOpen={() => getHappinessCalculation()}
                    showIcon
                    title={
                        <Row between="xs" middle="xs">
                            <Col x>{categoryTitleMap[categoryName]}</Col>
                            <Col>
                                <HappinessIcon
                                    happiness={
                                        provider.last_satisfaction_score?.[
                                            `${categoryName}_happiness`
                                        ]
                                    }
                                />
                            </Col>
                        </Row>
                    }>
                    <Divider sm />
                    {Object.entries(satisfactionRuleCategories[categoryName] || {}).map(
                        ([ruleName, rule]) => {
                            let hasExplicitOtherCase = false
                            const fields = [ruleName]
                            const boolCompareValues: boolean[] = []

                            const makeReadable = (str: string = '') =>
                                capitalize(
                                    str
                                        .replaceAll(categoryName + '_', '')
                                        .replaceAll('_', ' ')
                                        .replaceAll('perc', '%'),
                                )

                            const displayBool = (bl: boolean) => (bl ? 'Yes' : 'No')

                            const displayOtherCase = (index: number, caseScore: number) =>
                                fields.length === 1 && boolCompareValues.length === 2
                                    ? null
                                    : displayCaseCol(
                                          index,
                                          caseScore,
                                          fields.length === 1 && boolCompareValues.length === 1
                                              ? displayBool(!boolCompareValues[0])
                                              : 'Other',
                                      )

                            const displayCaseCol = (
                                index: number,
                                caseScore: number,
                                content: ReactNode,
                            ) => {
                                const selected =
                                    provider.last_satisfaction_score?.resultsObj?.[categoryName]?.[
                                        ruleName
                                    ] === index
                                const color =
                                    happinessColorMap[(Math.sign(caseScore) + 2) as 1 | 2 | 3]

                                return (
                                    <Col
                                        key={index}
                                        className={clsx(style.caseCol, selected && style.selected)}
                                        style={selected ? { color } : undefined}
                                        title={'score = ' + caseScore}
                                        x>
                                        {content}
                                    </Col>
                                )
                            }

                            return (
                                <Row key={ruleName} className={style.ruleRow}>
                                    <Col xs={3}>{makeReadable(ruleName)}</Col>
                                    <Col className={style.grow} x>
                                        <Row className={style.casesRow}>
                                            {rule.map((caseArr, i: number) => {
                                                const field = caseArr.at(-4) as string | undefined
                                                const operator = caseArr.at(-3) as
                                                    | string
                                                    | undefined
                                                const compareValue = caseArr.at(-2) as
                                                    | CompareValue
                                                    | undefined
                                                const caseScore = caseArr.at(-1) as number
                                                const fieldOrRuleName = field || ruleName

                                                if (field && !fields.includes(field)) {
                                                    fields.push(field)
                                                }

                                                const displayOperator =
                                                    !field && operator === '=' ? '' : operator
                                                const isExplicitOtherCase =
                                                    compareValue === undefined

                                                if (isExplicitOtherCase) {
                                                    hasExplicitOtherCase = true
                                                }

                                                let displayCompareValue = compareValue

                                                if (typeof compareValue === 'boolean') {
                                                    displayCompareValue = displayBool(compareValue)

                                                    if (!boolCompareValues.includes(compareValue)) {
                                                        boolCompareValues.push(compareValue)
                                                    }
                                                } else if (compareValue === null) {
                                                    displayCompareValue = '?'
                                                } else if (
                                                    fieldOrRuleName in customFieldValueDisplay
                                                ) {
                                                    displayCompareValue =
                                                        customFieldValueDisplay[
                                                            fieldOrRuleName as keyof typeof customFieldValueDisplay
                                                        ](compareValue)
                                                }

                                                return isExplicitOtherCase
                                                    ? displayOtherCase(i, caseScore)
                                                    : displayCaseCol(
                                                          i,
                                                          caseScore,
                                                          <>
                                                              {makeReadable(field)}{' '}
                                                              {displayOperator}{' '}
                                                              {displayCompareValue}
                                                          </>,
                                                      )
                                            })}
                                            {!hasExplicitOtherCase && displayOtherCase(-1, 0)}
                                        </Row>
                                    </Col>
                                </Row>
                            )
                        },
                    )}
                </Toggle>
            </Fragment>
        ))
    ) : (
        <LoadingDots />
    )
}

export default CustomerSatisfaction
